import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import { useTranslation } from 'react-i18next';
import i18n from './i18n';

export type TranslationFunction = ReturnType<typeof useTranslation>['t'];

export const DECIMAL_INPUT_REGEX = /^-?\d*(?:[.,]\d*)?$/;
export const DECIMAL_TWO_PLACES_INPUT_REGEX = /^-?\d*(?:[.,]\d{0,2})?$/;
// Prices (rent price, unit price, included-in-rent, extra positions) are stored
// with up to 4 decimal places so fractional unit pricing like 0,0250 is kept
// exactly; settlements round every amount derived from them to 2 places.
export const DECIMAL_FOUR_PLACES_INPUT_REGEX = /^-?\d*(?:[.,]\d{0,4})?$/;

const COUNTER_INPUT_SANITIZE_REGEX = /^-?\d*(?:[.,]\d*)?$/;

const getNumberLocale = () => (i18n.language === 'pl' ? 'pl-PL' : 'en-US');

const normalizeCounterNumber = (value: unknown): number | null => {
  if (value === null || value === undefined || value === "") return null;

  const sanitized = String(value)
    .trim()
    .replace(/[\s\u00A0\u202F]/g, "")
    .replace(/,/g, ".");

  if (!COUNTER_INPUT_SANITIZE_REGEX.test(sanitized)) return null;

  const integerPart = sanitized.split(".")[0];
  if (integerPart === "" || integerPart === "-") return null;

  const parsed = Number(integerPart);
  return Number.isFinite(parsed) ? parsed : null;
};

export const formatCounterProvider = (
  providerDisplay?: string | null,
  emailSourceDisplay?: string | null,
): string => {
  // When the email source (PDF / counter report) is known, it's more specific
  // than the generic "Automatic email" provider label, so show it instead.
  if (emailSourceDisplay) return emailSourceDisplay;

  return providerDisplay || "-";
};

export const formatNumber = (value: unknown): string => {
  if (value === null || value === undefined || value === "") return "-";

  const numValue = Number(value);
  if (Number.isNaN(numValue)) return String(value);

  const locale = getNumberLocale();

  return new Intl.NumberFormat(locale, {
    minimumFractionDigits: 2,
    // Prices can carry up to 4 decimal places (e.g. a 0,0250 unit price);
    // settlement money amounts are always stored rounded to 2.
    maximumFractionDigits: 4,
  }).format(numValue);
};

// Money with the currency attached, for the many places that render an amount
// followed by "zł". A missing amount stays a plain dash rather than becoming a
// currency-suffixed one.
export const formatMoney = (value: unknown): string => {
  const formatted = formatNumber(value);

  return formatted === "-" ? formatted : `${formatted} zł`;
};

// Quantity-like values (stock levels, ordered/used amounts, notification
// thresholds) are stored as decimals so a part held in fractional units stays
// possible, but they are whole numbers almost every time. Render only the
// decimals that are actually there, so a stock of 12 reads as "12" rather than
// "12,00" - unlike formatNumber, which pads money to 2 places on purpose.
export const formatQuantity = (value: unknown): string => {
  if (value === null || value === undefined || value === "") return "-";

  const numValue = Number(value);
  if (Number.isNaN(numValue)) return String(value);

  return new Intl.NumberFormat(getNumberLocale(), {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(numValue);
};

// Decimals come back from the API zero-padded ("100.00", "23.00"); show them in
// a form field the way someone would type them - trimmed, and with the comma
// this locale uses as its decimal mark. No thousands separators: this is the
// value a field shows while it HAS focus, and grouping mid-keystroke fights the
// caret. See formatDecimalForDisplay for the grouped form to show on blur.
export const formatDecimalForInput = (value: unknown): string => {
  if (value === null || value === undefined || value === "") return "";

  const parsed = Number(String(value).replace(",", "."));
  if (!Number.isFinite(parsed)) return String(value);

  return String(parsed).replace(".", ",");
};

// The inverse: takes whatever a user typed or pasted into a decimal field -
// comma or dot as the decimal mark, with or without the spaces formatNumber
// renders - and returns the dotted form the API stores.
export const toApiDecimal = (value: string): string =>
  value.replace(/[\s\u00A0\u202F]/g, "").replace(",", ".");

// Grouped display for a decimal field the user is NOT currently editing:
// "1234.5" reads back as "1 234,5". Pair it with formatDecimalForInput, which
// gives the raw form to show while the field has focus - applying grouping on
// every keystroke fights the caret, so fields swap between the two on
// focus/blur instead.
//
// However many decimals were typed are kept, so "1234,50" doesn't collapse to
// "1 234,5" the moment it loses focus.
export const formatDecimalForDisplay = (value: string): string => {
  if (!value || value === "-") return value;

  const normalized = toApiDecimal(value);
  const parsed = Number(normalized);
  if (!Number.isFinite(parsed)) return value;

  const decimals = Math.min((normalized.split(".")[1] ?? "").length, 20);

  return new Intl.NumberFormat(getNumberLocale(), {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(parsed);
};

// Progress / completion percentages: at most one decimal place, and never
// rounded up - a value like 99.6 must read as "99.6%", not a misleading "100%".
// Floors (truncates) rather than rounds so completion is never overstated.
export const formatCompletionPercent = (value: unknown): string => {
  const numValue = Number(value);
  if (Number.isNaN(numValue)) return "-";

  const floored = Math.floor(numValue * 10) / 10;

  return new Intl.NumberFormat(getNumberLocale(), {
    minimumFractionDigits: 0,
    maximumFractionDigits: 1,
  }).format(floored);
};

// Counter values are stored as decimals for future-proofing, but are always
// whole numbers in practice today - display them without decimal places.
export const formatCounterValue = (value: unknown): string => {
  const normalizedValue = normalizeCounterNumber(value);
  if (normalizedValue === null) return value === null || value === undefined || value === "" ? "-" : String(value);

  return new Intl.NumberFormat(getNumberLocale(), { maximumFractionDigits: 0 }).format(normalizedValue);
};

export const normalizeCounterInputValue = (value: string): string | null => {
  const sanitized = value.replace(/[\s\u00A0\u202F]/g, "").replace(/,/g, ".");
  if (!COUNTER_INPUT_SANITIZE_REGEX.test(sanitized)) return null;

  const sign = sanitized.startsWith("-") ? "-" : "";
  const unsignedValue = sign ? sanitized.slice(1) : sanitized;
  const integerPart = unsignedValue.split(".")[0].replace(/\D/g, "");

  if (!integerPart) return "";

  return `${sign}${integerPart}`;
};

export const formatCounterInputValue = (value: string): string => {
  if (value === "") return value;

  return formatCounterValue(value);
};

export const getMonthNames = (t: TranslationFunction): Record<number, string> => ({
  1: t("January"),
  2: t("February"),
  3: t("March"),
  4: t("April"),
  5: t("May"),
  6: t("June"),
  7: t("July"),
  8: t("August"),
  9: t("September"),
  10: t("October"),
  11: t("November"),
  12: t("December"),
});

/** Form-only sentinel; backend expands to Mon–Fri and never stores this value. */
export const WORKING_DAYS_WEEK_DAY = "0";

export const WEEK_DAYS = [
  { value: "1", labelKey: "Monday" },
  { value: "2", labelKey: "Tuesday" },
  { value: "3", labelKey: "Wednesday" },
  { value: "4", labelKey: "Thursday" },
  { value: "5", labelKey: "Friday" },
  { value: "6", labelKey: "Saturday" },
  { value: "7", labelKey: "Sunday" },
];

export const OPENING_HOUR_WEEK_DAY_OPTIONS = [
  { value: WORKING_DAYS_WEEK_DAY, labelKey: "Working days (Mon-Fri)" },
  ...WEEK_DAYS,
];

export const formatDateTime = (value: unknown): string => {
  if (!value) return '';
  const date = new Date(value as string);
  if (Number.isNaN(date.getTime())) return value as string;
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${year}-${month}-${day}\n${hours}:${minutes}`;
};

export const formatDate = (value: unknown): string => {
  if (!value) return '';
  const date = new Date(value as string);
  if (Number.isNaN(date.getTime())) return value as string;
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${day}-${month}-${year}`;
};

/** Consistent display for every table that shows a contract's end_date - keep
 * this the single source so the list, client/equipment contract tables and
 * the contract detail page don't drift from one another: no date at all
 * reads as "Indefinite"; a date flagged indefinite_after_end_date gets a
 * trailing "(∞)". Plain-string version for contexts that need one (DataGrid
 * `valueGetter`, used for sorting/filtering/export) - see
 * `ContractEndDateDisplay` below for the same thing rendered as a chip badge.
 * `endDate` is passed through as-is (already formatted upstream, or a raw
 * YYYY-MM-DD - this never reformats it). */
export const formatContractEndDate = (
  endDate: string | null | undefined,
  indefiniteAfterEndDate: boolean | null | undefined,
  t: TranslationFunction
): string => {
  if (!endDate) return t("Indefinite");
  if (indefiniteAfterEndDate) return `${endDate} (∞)`;
  return endDate;
};

/** JSX counterpart of `formatContractEndDate`, for contexts that can render a
 * cell/node instead of a plain string (DataGrid `renderCell`, `Typography`
 * children) - same wording, but the ∞ marker renders as a small badge
 * instead of an inline suffix, matching the status chips already used
 * elsewhere (e.g. the "Settled"/"Unsettled" chip on the contract detail
 * page). */
export const ContractEndDateDisplay = ({
  endDate,
  indefiniteAfterEndDate,
  t,
}: {
  endDate: string | null | undefined;
  indefiniteAfterEndDate: boolean | null | undefined;
  t: TranslationFunction;
}) => {
  if (!endDate) return <>{t("Indefinite")}</>;
  if (!indefiniteAfterEndDate) return <>{endDate}</>;

  return (
    <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.75 }}>
      {endDate}
      <Chip
        component="span"
        label="∞"
        size="small"
        variant="outlined"
        title={t("Continues indefinitely after this date")}
        sx={{
          height: 20,
          fontWeight: 700,
          "& .MuiChip-label": { px: 0.75, fontSize: "1.05rem" },
        }}
      />
    </Box>
  );
};

export const formatDateTimeBadge = (value: unknown) => {
  const formatted = formatDateTime(value);
  if (!formatted) return '';

  const [date, time] = formatted.split('\n');
  if (!time) return formatted;

  return (
    <span
      style={{
        display: 'inline-flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: 1,
        padding: '2px 6px',
        borderRadius: 8,
        background: 'var(--mui-palette-action-hover)',
        border: '1px solid var(--mui-palette-divider)',
        lineHeight: 1.15,
      }}
    >
      <span style={{ fontWeight: 600, fontSize: 12 }}>{date}</span>
      <span style={{ fontSize: 10, color: 'var(--mui-palette-text-secondary)' }}>{time}</span>
    </span>
  );
};

export const highlightMatch = (text: string, highlight: string) => {
  if (!highlight) return text;

  const parts = text.split(new RegExp(`(${highlight})`, "gi"));
  return (
    <span>
      {parts.map((part, index) =>
        part.toLowerCase() === highlight.toLowerCase() ? (
          <strong key={index}>{part}</strong>
        ) : (
          <span key={index}>{part}</span>
        )
      )}
    </span>
  );
};

export const formatAddress = (addr: any) => {
  if (!addr) return null;

  const parts = [
    addr.street,
    addr.city,
    addr.zip_code,
  ].filter(Boolean);

  return parts.join(", ");
};

export const getContrastColor = (hex: string): string => {
  let color = hex.replace("#", "");
  // Expand 3-digit hex to 6-digit
  if (color.length === 3) {
    color = color.split("").map((c) => c + c).join("");
  }
  const r = parseInt(color.substring(0, 2), 16);
  const g = parseInt(color.substring(2, 4), 16);
  const b = parseInt(color.substring(4, 6), 16);
  if (isNaN(r) || isNaN(g) || isNaN(b)) return "#000000";
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.5 ? "#000000" : "#ffffff";
};
